/**
 * 端口的视觉状态 —— 纯函数，零 Vue 依赖。
 *
 * **输入和输出必须走同一套逻辑。**
 *
 * 这里曾经是不对称的：`inputState` 会照 connectableSockets 算高亮，
 * 而 `outputState` 硬编码成 `{ dimmed: true, linkTarget: false }`。
 * 后果是「从未连线的输入端口反向拖、去找一个输出端口」时，
 * **该亮的输出端口一个都不亮** —— 用户会觉得「拖了半天没有落点」。
 *
 * 不对称本身就是 bug，所以合成一个函数，从构造上杜绝。
 */
import type { SocketDef } from '../types/node'

export interface SocketVisualState {
  connected: boolean
  dimmed: boolean
  linkTarget: boolean
  /** 本次拖拽的**起点**端口 */
  linkOrigin: boolean
}

const IDLE: SocketVisualState = {
  connected: false,
  dimmed: false,
  linkTarget: false,
  linkOrigin: false,
}

export function socketVisualState(
  def: SocketDef | undefined,
  linking: boolean,
  connectableSockets: readonly string[] | null | undefined,
  /** 本次拖拽起点的 handle id（不在本节点上传 null） */
  originSocketId?: string | null,
): SocketVisualState {
  if (!def) return IDLE

  const connected = Boolean(def.connected)
  // 没在拖线时，端口只反映连接态，不做高亮/变暗
  if (!linking) return { ...IDLE, connected }

  /*
   * 起点端口要单独标出来。
   *
   * 它既不是落点（不该有 linkTarget），也不该被当成「无关端口」变暗 ——
   * 否则拖线时连线从哪个端口出发完全看不出来。
   */
  if (originSocketId != null && def.id === originSocketId) {
    return { connected, dimmed: false, linkTarget: false, linkOrigin: true }
  }

  const ok = connectableSockets ? connectableSockets.includes(def.id) : false
  return { connected, dimmed: !ok, linkTarget: ok, linkOrigin: false }
}
